import { DvfModelGridMetrics } from '~/domain/dvf/dvfField';
import {
    DVF_QA_FOLDING_FRACTION,
    DVF_QA_GRID_DIMENSIONS,
    DVF_QA_MAX_JACOBIAN,
    DVF_QA_MIN_JACOBIAN,
} from './deformableRegistrationTags';

export const IDENTITY_MATRIX_4X4: readonly number[] = [
    1, 0, 0, 0, 0, 1, 0, 0, 0, 0, 1, 0, 0, 0, 0, 1,
];

// Exactly `count` values, every one finite; anything else is malformed.
export function finiteValues(
    values: readonly unknown[] | undefined,
    count: number
): number[] | null {
    if (!values || values.length !== count) {
        return null;
    }
    const numbers = values.map(Number);
    return numbers.every((v) => Number.isFinite(v)) ? numbers : null;
}

export function finiteTriple(
    values: readonly unknown[] | undefined
): [number, number, number] | null {
    const numbers = finiteValues(values, 3);
    return numbers ? [numbers[0], numbers[1], numbers[2]] : null;
}

// An absent matrix sequence is the identity; a present one must hold 16 finite values, or the grid is malformed.
export function deformationMatrix(
    present: boolean,
    values: readonly unknown[] | undefined
): number[] | null {
    return present ? finiteValues(values, 16) : [...IDENTITY_MATRIX_4X4];
}

// The moving scan's frame: the one related frame other than the fixed one; none or several keep the fixed frame.
export function movingFrameOfReferenceUid(
    fixedFrameOfReferenceUid: string,
    relatedFrameOfReferenceUids: readonly string[]
): string {
    if (relatedFrameOfReferenceUids.length > 1) {
        console.warn(
            `Deformable registration relates ${relatedFrameOfReferenceUids.length} frames of reference; it will not pair with an image.`
        );
    }
    return relatedFrameOfReferenceUids.length === 1
        ? relatedFrameOfReferenceUids[0]
        : fixedFrameOfReferenceUid;
}

// Reads one private DVF QA value by its offset in the block, as `count` values of `bytesPerValue` bytes.
export type DvfQaValueReader = (
    offset: number,
    count: number,
    bytesPerValue: 4 | 8
) => number[] | null;

export function modelGridMetrics(
    read: DvfQaValueReader
): DvfModelGridMetrics | null {
    const minJacobian = read(DVF_QA_MIN_JACOBIAN, 1, 8);
    const maxJacobian = read(DVF_QA_MAX_JACOBIAN, 1, 8);
    const folding = read(DVF_QA_FOLDING_FRACTION, 1, 8);
    const size = read(DVF_QA_GRID_DIMENSIONS, 3, 4);
    if (!minJacobian || !maxJacobian || !folding || !size) {
        return null;
    }
    return {
        minJacobian: minJacobian[0],
        maxJacobian: maxJacobian[0],
        foldingFraction: folding[0],
        measuredSize: [size[0], size[1], size[2]],
    };
}

// Little-endian private values from their bytes; the byte count must match exactly.
export function privateValuesFromBytes(
    view: DataView,
    count: number,
    bytesPerValue: 4 | 8
): number[] | null {
    if (view.byteLength !== count * bytesPerValue) {
        return null;
    }
    const values = Array.from({ length: count }, (_unused, index) =>
        bytesPerValue === 8
            ? view.getFloat64(index * 8, true)
            : view.getUint32(index * 4, true)
    );
    return values.every((v) => Number.isFinite(v)) ? values : null;
}
